/**
 * DI-3-C — the "Project" entry of the intent menu: the pilot's OWN projects.
 *
 *   GET /api/projects/v1/projects?pageSize=50
 *       &state=DATA_VALIDATION_CUSTOMER,PREPARATION,EXECUTION
 *       &sortBy=projectName   (projects/v1 2.25.0; `scope` omitted = own)
 *
 * M3C-10 / DI-D66 — only a project in DATA_VALIDATION_CUSTOMER, PREPARATION or
 * EXECUTION accepts a pilot's drop; the backend refuses every other state
 * with 422 `project_not_open` (inspection-3/v1 2.5.0 on the standalone create,
 * intake/v1 1.32.0 on Move onto a turbine with a project). The rule lives in
 * the backend; `OWN_PROJECTS_PATH`'s `state` filter is the frontend's ONLY
 * copy of it (a unit test asserts the exact string). `state` takes precedence
 * over `includeCompleted` server-side (SB4-05 F-05), so that one is dropped.
 *
 * Own scope only (DI-3-B §5 a): the standalone create under a project accepts
 * only the caller's own project (a project shared by another tenant lives in
 * the owner's schema → 404), so a member project is never offered. `GET
 * /projects` is `JwtAuthGuard` only — a pilot may list. Each item carries
 * `workflow` / `workflowLabel` (PM-WF-01): null = not set → the row's picker.
 *
 * The projects feature's `useProjectsListQuery` is not `enabled`-gated on
 * tenant readiness (the repo rule for tenant-scoped queries), so the intake
 * reads the same endpoint through its own gated query.
 */
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@aeriosiq/auth-client";
import { projectsFetch } from "../../projects/projectsFetch.js";
import type {
  ProjectListItem,
  ProjectListResponse,
} from "../../projects/types.js";
import { intakeKeys } from "./keys.js";

export const OWN_PROJECTS_PATH =
  "/projects?page=1&pageSize=50&state=DATA_VALIDATION_CUSTOMER,PREPARATION,EXECUTION&sortBy=projectName&sortDir=asc";

export function useOwnProjects() {
  const tenantReady = useAuthStore((s) => Boolean(s.tenant?.tenantSlug));
  return useQuery<ProjectListResponse>({
    queryKey: intakeKeys.ownProjects(),
    queryFn: () => projectsFetch<ProjectListResponse>(OWN_PROJECTS_PATH),
    enabled: tenantReady,
    staleTime: 60_000,
    retry: false,
  });
}

/** Own rows only — a row without `ownership` (pre-2.4.0) counts as own. */
export function ownProjects(
  res: ProjectListResponse | undefined,
): ProjectListItem[] {
  return (res?.items ?? []).filter((p) => (p.ownership ?? "owner") === "owner");
}

/**
 * M3C-10 — a 422 `project_not_open {projectId, state}` in words: the
 * standalone create (inspection-3/v1 2.5.0) and Move onto a turbine with a
 * project (intake/v1 1.32.0). The server's `state` is always named (a row
 * without one reads "no state"), never swallowed.
 */
export function projectNotOpenWords(state: unknown): string {
  const named =
    typeof state === "string" && state.trim() !== "" ? state : "no state";
  return `This project is not open for new data sets (${named}) — ask the project manager.`;
}
